import { camelCase } from 'lodash';

import { useProfile } from 'context/profile';
import { PERMISSIONS } from 'models/Profile';
import { WorkflowAction } from 'models/WorkflowAction';

/**
 * The one place the referral experience asks "may this user do that" (OLIO-2615).
 *
 * These gates are UX only. The API enforces the same rules and 403s regardless, so a component
 * hiding a button is saving someone a pointless click, never protecting anything.
 */
export default function useWorkflowPermissions() {
  const { profile } = useProfile();

  const canView = profile.has(PERMISSIONS.workflowInboundReferralView);
  const canCreate = profile.has(PERMISSIONS.workflowInboundReferralCreate);
  const canEdit = profile.has(PERMISSIONS.workflowInboundReferralEdit);

  /**
   * Whether the user may act on one action: complete, save progress, re-run, dismiss, amend.
   *
   * Workflow-level edit is a superset, so an editor can act on every kind. A kind's own grant
   * exists for the narrow specialist who holds nothing else, and a kind with no grant of its own
   * (`editPermissionName: null`) rides workflow edit alone.
   *
   * The name arrives snake_case while `profile.permissions` is camelized, hence the conversion.
   * lodash camelCase would mangle an acronym (`ai` -> `Ai` where the payload says `AI`), which no
   * kind name contains today; a kind that introduces one needs a real mapping instead.
   */
  const canEditAction = (action: WorkflowAction) => {
    if (canEdit) return true;
    if (!action.editPermissionName) return false;

    // Cast because `permissions` is typed to the PERMISSIONS keys, and the per-kind names are
    // deliberately absent from that map — the API owns which kind needs which grant.
    const held = profile.permissions as Record<string, boolean | undefined> | undefined;

    return !!held?.[camelCase(action.editPermissionName)];
  };

  return { canView, canCreate, canEdit, canEditAction };
}
